import React, { useEffect, useState } from 'react';
import { formatBytes } from '../../videoUpload/videoCaps';
import { describeClipIssues } from '../../utils/footageConcat';
import { footageCountLabel } from '../../lib/operationCopy';
import { normalizeRotation } from '../../videoUpload/rotation';

// The footage list for a multi-video project: one row per clip in playback
// order, showing what the server probe has learned so far.
//
// A row's right-hand side is a state readout, not decoration. Until the ingest
// trigger has probed the file there is no duration, resolution or poster to
// show — the browser could not read them either (Brinno writes MJPEG inside an
// .mp4, which no <video> element will decode) — so the row says "Reading
// file…" rather than showing blanks that look like data loss.
//
// Phase 3 makes the list editable, because the order shown here IS the order
// the combined video plays in and a clip stuck in `status: 'error'` blocks
// combining outright (the callable refuses with FOOTAGE_NOT_READY and tells the
// user to remove it — which, until now, they could not do).
//
// The reorder interaction is lifted from VideoUploadDialog verbatim — drag,
// plus keyboard-reachable ↑/↓ buttons, same aria-labels — because this is the
// same list the user arranged in the preflight, and two different gestures for
// one job is how a user learns not to trust either.

const formatClock = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const STATUS_LABEL = {
  uploading: 'Uploading…',
  probing: 'Reading file…',
  error: 'Could not read this file',
};

const FootageVideoList = ({
  videos,
  posterUrls,
  // videoKey → the clip's `details.issues` entries (server-returned or locally
  // predicted). Renders as a badge on the row, so "which clip is holding this
  // up" is answered where the clip is, not in a paragraph under the CTA.
  issues = null,
  // (fromIndex, toIndex) → persists the new playback order. Absent = read-only.
  onMove = null,
  // A combine or an upload is in flight. Reordering mid-combine would edit the
  // playback order of a video already being written, and mid-upload the list is
  // a partial snapshot — the arriving clips are stamped with orders derived
  // from what is stored, so re-stamping underneath them scrambles both.
  mutationsDisabled = false,
  mutationsDisabledReason = null,
  // (video) → Promise. Rejects with an Error whose message is already
  // user-facing; this component only decides WHERE to show it.
  onDelete = null,
  // The project's footage rotation, applied to every poster. Project-scoped,
  // so the whole list turns together — a tab where the player is the right way
  // up and the thumbnails beneath it are not looks broken even though both are
  // showing the same footage.
  rotation = 0,
  // Optional node parked at the end of the chips row — the collapsed rotate
  // control (FootageRotationControl in `compact` mode) on an unrotated project.
  // Passed in rather than built here because the tab owns the lifecycle rule
  // for WHICH rotate control is live; this component only owns the row it sits
  // in. Absent by default, so a list rendered without it is unchanged.
  rotateControl = null,
}) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [confirmingKey, setConfirmingKey] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // Release the "Removing…" state when the row actually leaves the list, rather
  // than inline on success (which flashes the buttons back to normal for the
  // frame before the subscription drops the row).
  //
  // Needed because footage doc ids are content-addressed and deterministic —
  // buildVideoKey() is `${seconds}-${size}-${name}`, so re-uploading the file
  // just deleted brings back a row holding the id still parked in deletingKey.
  // Without this, that row's ✕ renders a permanently disabled "Removing…" for
  // the life of the mount, and the clip can never be removed again.
  useEffect(() => {
    if (deletingKey && !(videos || []).some((video) => video.id === deletingKey)) {
      setDeletingKey(null);
    }
  }, [videos, deletingKey]);

  if (!videos || videos.length === 0) return null;

  const totalBytes = videos.reduce((a, v) => a + (v.sizeBytes || 0), 0);
  const probed = videos.filter((v) => Number.isFinite(v.durationSec));
  const totalSeconds = probed.reduce((a, v) => a + v.durationSec, 0);
  const posterRotation = normalizeRotation(rotation);
  const editable = !!onMove && !mutationsDisabled;
  const deletable = !!onDelete && !mutationsDisabled;

  const handleDrop = (targetIndex) => {
    if (dragIndex == null || !editable) return;
    onMove(dragIndex, targetIndex);
    setDragIndex(null);
  };

  const runDelete = async (video) => {
    setDeleteError(null);
    setDeletingKey(video.id);
    try {
      await onDelete(video);
      // Deliberately NOT clearing deletingKey on success: the row is about to
      // disappear from the live subscription, and clearing it first flashes the
      // buttons back to normal for one frame.
      setConfirmingKey(null);
    } catch (err) {
      setDeletingKey(null);
      setDeleteError({ videoKey: video.id, message: err?.message || 'Could not remove this clip.' });
    }
  };

  return (
    <section className="pi-footage-list" data-testid="footage-video-list">
      <div className="pi-header-chips">
        {/* "clips", from operationCopy — the same word the CTA below and the
            dropzone use. This chip used to say "videos" while the chip directly
            under it said "clips", for the same four objects. */}
        <span className="pi-header-chip">{footageCountLabel(videos.length)}</span>
        <span className="pi-header-chip">{formatBytes(totalBytes)}</span>
        {probed.length === videos.length && totalSeconds > 0 && (
          <span className="pi-header-chip">{formatClock(totalSeconds)} of footage</span>
        )}
        {rotateControl}
      </div>
      <ol className="pi-video-list">
        {videos.map((video, index) => {
          const status = video.status || 'probing';
          const ready = status === 'ready';
          const posterUrl = posterUrls?.[video.id];
          const issueText = describeClipIssues(issues?.[video.id]);
          const confirming = confirmingKey === video.id;
          const deleting = deletingKey === video.id;
          const rowError = deleteError?.videoKey === video.id ? deleteError.message : null;
          return (
            <li
              className={`pi-video-row${dragIndex === index ? ' pi-video-row--dragging' : ''}${issueText ? ' pi-video-row--issue' : ''}`}
              key={video.id}
              data-testid={`footage-row-${index}`}
              draggable={editable}
              onDragStart={editable ? () => setDragIndex(index) : undefined}
              onDragOver={editable ? (e) => e.preventDefault() : undefined}
              onDrop={editable ? () => handleDrop(index) : undefined}
              onDragEnd={editable ? () => setDragIndex(null) : undefined}
            >
              <span className="pi-video-row-idx">{index + 1}</span>
              <span className="pi-video-row-poster">
                {posterUrl
                  ? (
                    <img
                      src={posterUrl}
                      alt=""
                      className={posterRotation ? `pi-poster-rot-${posterRotation}` : undefined}
                      data-testid={`footage-poster-${index}`}
                    />
                  )
                  : <span className="pi-video-row-poster-empty" aria-hidden="true" />}
              </span>
              <span className="pi-video-row-name" title={video.fileName}>
                {video.fileName || video.id}
                {issueText && (
                  <span className="pi-video-row-issue" data-testid={`footage-issue-${index}`}>
                    {issueText}
                  </span>
                )}
                {rowError && (
                  <span className="pi-video-row-issue" data-testid={`footage-delete-error-${index}`}>
                    {rowError}
                  </span>
                )}
              </span>
              <span className="pi-video-row-size">{formatBytes(video.sizeBytes)}</span>
              {/* A row carrying an issue badge has already said, in red and
                  next to the file name, what is wrong with it. Repeating
                  "Could not read this file" here in the same grey used for
                  `1920x1080 · 30 fps` is the sentence twice — and that grey
                  copy is precisely what made a broken row look like a healthy
                  one. The status label still stands alone on a list rendered
                  without an `issues` map. */}
              <span className="pi-video-row-dur">
                {ready
                  ? formatClock(video.durationSec)
                  : (issueText ? '—' : (STATUS_LABEL[status] || '—'))}
              </span>
              <span className="pi-video-row-meta">
                {ready && video.width && video.height ? `${video.width}x${video.height}` : ''}
                {ready && video.fps ? ` · ${Math.round(video.fps)} fps` : ''}
              </span>
              {(onMove || onDelete) && (
                <span className="pi-video-row-actions">
                  {confirming ? (
                    // Two steps, because this delete is not undoable: the clip's
                    // object is removed from storage and its bytes released.
                    //
                    // The two buttons are deliberately NOT twins. They sat
                    // side by side as two identical light buttons, which is how
                    // an irreversible action gets clicked by muscle memory —
                    // the confirm is the red one, the way out is the quiet one.
                    <>
                      <span className="pi-video-confirm-q">Remove?</span>
                      <button
                        type="button"
                        className="btn pi-btn-sm pi-video-confirm-danger"
                        data-testid={`footage-delete-confirm-${index}`}
                        disabled={deleting}
                        onClick={() => runDelete(video)}
                      >
                        {deleting ? 'Removing…' : 'Remove'}
                      </button>
                      <button
                        type="button"
                        className="btn pi-btn-sm pi-video-confirm-cancel"
                        data-testid={`footage-delete-cancel-${index}`}
                        disabled={deleting}
                        onClick={() => setConfirmingKey(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      {onMove && (
                        <>
                          {/* Buttons, not drag alone: drag-and-drop is
                              unreachable by keyboard and unusable on touch, and
                              playback order is the one thing this list must not
                              make optional. */}
                          <button
                            type="button"
                            className="pi-video-move"
                            aria-label={`Move ${video.fileName || video.id} earlier`}
                            disabled={!editable || index === 0}
                            onClick={() => onMove(index, index - 1)}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="pi-video-move"
                            aria-label={`Move ${video.fileName || video.id} later`}
                            disabled={!editable || index === videos.length - 1}
                            onClick={() => onMove(index, index + 1)}
                          >
                            ↓
                          </button>
                        </>
                      )}
                      {onDelete && (
                        <button
                          type="button"
                          className="pi-video-move pi-video-remove"
                          aria-label={`Remove ${video.fileName || video.id}`}
                          disabled={!deletable}
                          onClick={() => { setDeleteError(null); setConfirmingKey(video.id); }}
                        >
                          ✕
                        </button>
                      )}
                    </>
                  )}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {mutationsDisabled && mutationsDisabledReason && (
        <p className="pi-footage-list-note" data-testid="footage-list-locked">
          {mutationsDisabledReason}
        </p>
      )}
    </section>
  );
};

export default FootageVideoList;
